import { useEffect } from "react";
import { X, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/smartfile/TextInput";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { usePatientQuickTags } from "@/hooks/usePatientQuickTags";

interface PatientInfo {
  name: string;
  birth_date: string | null;
  avatar_url?: string | null;
}

interface TextInputDialogProps {
  onSubmit: (text: string) => void;
  onClose: () => void;
  patient?: PatientInfo;
  patientId?: string;
}

const calculateAge = (birthDate: string | null): number | null => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

export function TextInputDialog({ onSubmit, onClose, patient, patientId }: TextInputDialogProps) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const { tags } = usePatientQuickTags(patientId);

  const initials = patient?.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

  const age = patient ? calculateAge(patient.birth_date) : null;

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm">
      <div className="relative h-full w-full overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background/80 backdrop-blur px-4 py-3">
          <h2 className="text-lg font-semibold">Entrada de Texto</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {patient && (
          <div className="border-b bg-muted/30 px-4 py-3">
            <div className="container mx-auto max-w-3xl">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar className="h-10 w-10 flex-shrink-0">
                    {patient.avatar_url && <AvatarImage src={patient.avatar_url} alt={patient.name} />}
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col min-w-0">
                    <span className="font-semibold text-foreground truncate">{patient.name}</span>
                    {age !== null && (
                      <span className="text-sm text-muted-foreground">{age} anos</span>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onClose}
                  className="flex-shrink-0 gap-2"
                >
                  <User className="h-4 w-4" />
                  Ver paciente
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="container mx-auto max-w-3xl p-4">
          <TextInput
            quickTags={tags}
            onSubmit={(text) => {
              onSubmit(text);
              onClose();
            }}
            onCancel={onClose}
          />
        </div>
      </div>
    </div>
  );
}
